import React, { FunctionComponent, useCallback, useEffect } from 'react'
import {
	Asset,
	getAssetDescription,
	overarchingTypeOf,
} from '#modules/AssetOverview/AssetOverview.types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import {
	CurrencyDisplay,
	H2Flow,
	InfoTooltip,
	useIntlConfig,
	useModal,
} from '@monthio/react-components'
import {
	AssetBudgetExpenseEstimateModal,
	AssetBudgetExpenseEstimateModalResult,
} from './AssetBudgetExpenseEstimateModal'
import { useFormikContext } from 'formik'
import { AssetCard } from '#modules/AssetOverview/Components/AssetCard'
import { ExpenseEstimateDto } from '#modules/Budget/types/expense.types'
import {
	AssetBudgetDto,
	ExpenseEstimateBucketDto,
} from '../AssetExpenses.types'
import {
	RowsWithHeadingContainer,
	RowWithHeading,
} from '../../../modulesTemp/general/FlowWizardUtils'

interface AssetBudgetProps {
	asset?: Asset
	assetBudget: AssetBudgetDto
	formInputId: string
	handleNotFoundAsset: () => void
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`
const CurrencyDisplayContainer = styled.div`
	display: flex;
`
const TooltipContainer = styled.div`
	margin-right: 5px;
`

export const AssetExpenseEstimateTotalRow: FunctionComponent<{
	amount: number
}> = ({ amount }) => {
	const { t } = useTranslation()

	return (
		<RowsWithHeadingContainer>
			<RowWithHeading heading={t('assetExpenses-assetBudget-total')}>
				<H2Flow>
					<CurrencyDisplay amount={Math.abs(Math.round(amount))} />
				</H2Flow>
			</RowWithHeading>
		</RowsWithHeadingContainer>
	)
}

export const AssetBudget: FunctionComponent<AssetBudgetProps> = ({
	asset,
	assetBudget,
	formInputId,
	handleNotFoundAsset,
}) => {
	const { t } = useTranslation()
	const { openModal } = useModal()
	const { setFieldValue } = useFormikContext()
	const intlConfig = useIntlConfig({ showCurrency: true })

	const handleClick = useCallback(
		async (
			expenseEstimateBucket: ExpenseEstimateBucketDto,
			fieldId: string,
		) => {
			if (!asset) {
				return
			}
			const handleModalSave = (
				expenseEstimates: ExpenseEstimateDto[],
				monthlyAmount: number,
			) => {
				setFieldValue(`${fieldId}.expenseEstimates`, expenseEstimates)
				setFieldValue(`${fieldId}.monthlyAmount`, monthlyAmount)
			}

			if (
				['carMaintenance', 'housingMaintenance'].includes(
					expenseEstimateBucket.bucket,
				) &&
				expenseEstimateBucket.expenseEstimates.length > 0
			) {
				expenseEstimateBucket.expenseEstimates = [
					{
						...expenseEstimateBucket.expenseEstimates[0]!,
						monthlyAmount: expenseEstimateBucket.expenseEstimates
							.map((x) => x.monthlyAmount)
							.reduce((sum, next) => sum + next, 0),
					},
				]
			}

			const bodyRender = ({
				close,
			}: {
				close: (
					result: AssetBudgetExpenseEstimateModalResult | null | false,
				) => void
			}) => {
				return (
					<div>
						<AssetBudgetExpenseEstimateModal
							assetId={asset.id.toString()}
							expenseBucket={expenseEstimateBucket.bucket}
							onClose={close}
							expenseEstimates={expenseEstimateBucket.expenseEstimates}
							monthlyEstimate={expenseEstimateBucket.monthlyAmount}
							amountModifiedByCreditPolicy={
								expenseEstimateBucket.amountModifiedByCreditPolicy
							}
						/>
					</div>
				)
			}

			bodyRender.displayName = 'ExpensesBucket'
			const result = await openModal({
				title: t(
					`assetExpenses-bucket-${expenseEstimateBucket.bucket}-modalTitle`,
					{
						assetName: getAssetDescription(asset).title,
					},
				),
				maxWidth: 'sm',
				bodyRender,
			})

			if (result) {
				const modalResult = result as AssetBudgetExpenseEstimateModalResult
				handleModalSave(modalResult.expenseEstimates, modalResult.monthlyAmount)
			}
		},
		[asset, openModal, setFieldValue, t],
	)

	useEffect(() => {
		if (!asset) {
			console.error(`Asset ${assetBudget.assetId} not found`)
			handleNotFoundAsset()
		}
	}, [assetBudget.assetId, asset, handleNotFoundAsset])

	if (!asset) {
		return null
	}

	const totalAmount = (() => {
		const sum = assetBudget.expenseEstimateBuckets
			.map((x) => x.monthlyAmount)
			.reduce((a, b) => a + b, 0)

		return sum
	})()

	return (
		<Container>
			<AssetCard asset={asset} variant="preview" />
			<RowsWithHeadingContainer>
				{assetBudget.expenseEstimateBuckets.map(
					(expenseEstimateBucket, index) => (
						<RowWithHeading
							heading={
								<InfoTooltip
									tooltip={t(
										`assetExpenses-budgetBucket-${expenseEstimateBucket.bucket}-tooltip`,
									)}
									variant="search"
								>
									{t(
										`assetExpenses-budgetBucket-${expenseEstimateBucket.bucket}`,
									)}
								</InfoTooltip>
							}
							key={`BUCKET_${expenseEstimateBucket.bucket}`}
							onClick={() =>
								handleClick(
									expenseEstimateBucket,
									`${formInputId}.expenseEstimateBuckets[${index}]`,
								)
							}
						>
							<CurrencyDisplayContainer>
								{expenseEstimateBucket.amountModifiedByCreditPolicy &&
								expenseEstimateBucket.monthlyAmount !==
									expenseEstimateBucket.expenseEstimates
										.map((x) => x.monthlyAmount)
										.reduce((sum, next) => sum + next, 0) ? (
									<TooltipContainer style={{ marginRight: 5 }}>
										<InfoTooltip
											tooltip={t(
												`assetExpenses-valueModifiedByCreditpolicy-${overarchingTypeOf(asset.assetType)}`,
											)}
											variant="info"
										/>
									</TooltipContainer>
								) : null}
								<CurrencyDisplay
									amount={expenseEstimateBucket.monthlyAmount}
									currencyFormatOptions={{
										allowNegativeValue: false,
										decimalsLimit: 0,
										intlConfig,
										maxLength: 8,
									}}
								/>
							</CurrencyDisplayContainer>
						</RowWithHeading>
					),
				)}
			</RowsWithHeadingContainer>
			<AssetExpenseEstimateTotalRow amount={Math.round(totalAmount)} />
		</Container>
	)
}

// assetExpenses-budgetBucket-other
// assetExpenses-budgetBucket-insurance
// assetExpenses-budgetBucket-rent
// assetExpenses-budgetBucket-homeLoan
// assetExpenses-budgetBucket-mortgageLoan
// assetExpenses-budgetBucket-homeOwnersAssociation
// assetExpenses-budgetBucket-cooperativeHousing
// assetExpenses-budgetBucket-utilities
// assetExpenses-budgetBucket-carLeasing
// assetExpenses-budgetBucket-housingMaintenance
// assetExpenses-budgetBucket-fuel
// assetExpenses-budgetBucket-carLoan
// assetExpenses-budgetBucket-vehicleFees
// assetExpenses-budgetBucket-carMaintenance

// assetExpenses-budgetBucket-other-tooltip
// assetExpenses-budgetBucket-insurance-tooltip
// assetExpenses-budgetBucket-rent-tooltip
// assetExpenses-budgetBucket-homeLoan-tooltip
// assetExpenses-budgetBucket-mortgageLoan-tooltip
// assetExpenses-budgetBucket-homeOwnersAssociation-tooltip
// assetExpenses-budgetBucket-cooperativeHousing-tooltip
// assetExpenses-budgetBucket-utilities-tooltip
// assetExpenses-budgetBucket-carLeasing-tooltip
// assetExpenses-budgetBucket-housingMaintenance-tooltip
// assetExpenses-budgetBucket-fuel-tooltip
// assetExpenses-budgetBucket-carLoan-tooltip
// assetExpenses-budgetBucket-vehicleFees-tooltip
// assetExpenses-budgetBucket-carMaintenance-tooltip
